/**
 * FanSpeed Style01 - Stitch 設計：標題 + mode_fan 圖示、四格 Low/Med/High/Auto
 * 預設 LOW,MED,HIGH,AUTO，實際來源 attributes；alias 來自 customControls.SettingSynonym
 * 互動：點擊後樂觀更新（localOverrides）
 */
import { useState, useEffect, useMemo } from 'react'
import type { TraitControlProps } from './types'
import {
  resolveModesFromAttributes,
  resolveFanSpeedLabelsFromCustomControls,
  FAN_SPEED_DISPLAY_LABELS,
} from './traitStyleConfig'
import './FanSpeedStyle01.css'

const STITCH_ORDER = ['low', 'medium', 'high', 'auto']

export function FanSpeedStyle01({
  trait,
  device,
  state,
  attributes,
  onCommand,
  disabled = false,
}: TraitControlProps) {
  const speedIds = useMemo(() => {
    const ids = resolveModesFromAttributes(attributes, trait)
    if (ids.length >= 4) return ids.slice(0, 4)
    const fallback = ['low', 'medium', 'high', 'auto']
    return ids.length ? ids : fallback
  }, [attributes, trait])

  const orderedIds = useMemo(() => {
    const set = new Set(speedIds.map((s) => s.toLowerCase()))
    const result = STITCH_ORDER.filter((id) => set.has(id))
    const rest = speedIds.filter((s) => !STITCH_ORDER.includes(s.toLowerCase()))
    return [...result, ...rest].slice(0, 4)
  }, [speedIds])

  const endpoint = useMemo(() => {
    const sn = device?.deviceSN ?? device?.sn ?? ''
    return sn.includes('#') ? sn.split('#')[1] || '1' : '1'
  }, [device?.deviceSN, device?.sn])

  const labelMap = useMemo(() => {
    const fromCustom = resolveFanSpeedLabelsFromCustomControls(
      device?.customControls as Record<string, unknown> | undefined,
      endpoint,
      orderedIds,
      'en'
    )
    return { ...FAN_SPEED_DISPLAY_LABELS, ...fromCustom }
  }, [device?.customControls, endpoint, orderedIds])

  const fromState = (state?.currentFanSpeedSetting ?? state?.fanSpeed ?? state?.fan_speed ?? 'high') as string
  const [localOverride, setLocalOverride] = useState<string | null>(null)

  useEffect(() => {
    setLocalOverride(null)
  }, [fromState])

  const current = (localOverride ?? fromState ?? 'high').toLowerCase()

  const handleSelect = (id: string) => {
    if (disabled) return
    const normalized = id.toLowerCase()
    setLocalOverride(normalized)
    void onCommand('setFanSpeed', { fanSpeed: id })
  }

  return (
    <div className="trait-fanspeed-style01 trait-stitch-glass">
      <div className="trait-fanspeed-style01__header">
        <span className="trait-fanspeed-style01__title">Fan Speed</span>
        <span className="trait-fanspeed-style01__icon-wrap">
          <span
            className={`material-symbols-outlined trait-fanspeed-style01__icon trait-fanspeed-style01__icon--${current}`}
            aria-hidden
          >
            mode_fan
          </span>
          {current === 'auto' && (
            <span className="trait-fanspeed-style01__icon-badge" aria-hidden>A</span>
          )}
        </span>
      </div>
      <div className="trait-fanspeed-style01__grid">
        {orderedIds.map((id) => {
          const label = labelMap[id] ?? labelMap[id.toLowerCase()] ?? id
          const isActive = current === id.toLowerCase()
          return (
            <button
              key={id}
              type="button"
              className={`trait-fanspeed-style01__btn ${isActive ? 'active' : ''}`}
              onClick={() => handleSelect(id)}
              disabled={disabled}
            >
              {label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
